import { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Box, Star, File, Link as LinkIcon, User } from 'lucide-react';
import { Button } from '@/components/Button';
import { DetailHeader } from '@/components/DetailHeader';
import { DeleteButton } from '@/components/DeleteButton';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Spinner } from '@/components/Spinner';
import { SlideOver } from '@/components/SlideOver';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { ModelDetailView } from '@/features/models/components/ModelDetailView';
import { useModel, useUpdateModel, useDeleteModel } from '@/features/models/hooks';
import { useToast } from '@/components/ToastProvider';
import type { UpdateModelInput } from '@maker-toolchest/shared';
import type { LocationStateWithContext } from '@/types/navigation';

export function ModelDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [formData, setFormData] = useState<UpdateModelInput>({});

  const { data: model, isLoading, error } = useModel(id!);
  const updateModel = useUpdateModel();
  const deleteModel = useDeleteModel();

  // Read navigation context from location state (for dynamic back button)
  const locationState = location.state as LocationStateWithContext | undefined;
  const previousContext = locationState?.from;

  const handleEdit = () => {
    if (!model) return;
    setFormData({
      name: model.name,
      description: model.description || undefined,
      notes: model.notes || undefined,
      isFavorite: model.isFavorite,
    });
    setIsEditFormOpen(true);
  };

  const handleUpdateModel = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    try {
      await updateModel.mutateAsync({ id, input: formData });
      toast.success('Model updated successfully');
      setIsEditFormOpen(false);
    } catch (error: any) {
      toast.error(error.message || 'Failed to update model');
    }
  };

  const handleBack = () => {
    navigate('/models');
  };

  const handleToggleFavorite = async () => {
    if (!model) return;

    try {
      await updateModel.mutateAsync({
        id: model.id,
        input: { isFavorite: !model.isFavorite },
      });
      toast.success(model.isFavorite ? 'Removed from favorites' : 'Added to favorites');
    } catch (error: any) {
      toast.error(error.message || 'Failed to update favorite status');
    }
  };

  const handleDeleteModel = async () => {
    if (!model) return;

    try {
      await deleteModel.mutateAsync(model.id);
      toast.success('Model deleted successfully');
      setShowDeleteConfirm(false);
      navigate('/models');
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete model');
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !model) {
    return (
      <div className="p-4">
        <div className="mb-4">
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<ArrowLeft className="h-4 w-4" />}
            onClick={handleBack}
          >
            Back to Models
          </Button>
        </div>
        <div className="rounded-lg border border-border bg-background-subtle p-8 text-center">
          <p className="text-error">
            {error ? 'Failed to load model details' : 'Model not found'}
          </p>
          <Button variant="secondary" onClick={handleBack} className="mt-4">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  // Build metadata for header
  const metadata = (
    <div className="flex items-center gap-3">
      {model.files.length > 0 && (
        <span className="text-xs text-foreground-muted flex items-center gap-1">
          <File className="h-3 w-3" />
          {model.files.length} {model.files.length === 1 ? 'file' : 'files'}
        </span>
      )}
      {model.links.length > 0 && (
        <span className="text-xs text-foreground-muted flex items-center gap-1">
          <LinkIcon className="h-3 w-3" />
          {model.links.length} {model.links.length === 1 ? 'link' : 'links'}
        </span>
      )}
      {model.author && (
        <span className="text-xs text-foreground-muted flex items-center gap-1">
          <User className="h-3 w-3" />
          {model.author}
        </span>
      )}
    </div>
  );

  // Build actions for header
  const actions = (
    <>
      <button
        onClick={handleToggleFavorite}
        disabled={updateModel.isPending}
        className="flex-shrink-0 transition-opacity hover:opacity-70 disabled:opacity-50"
        title={model.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
      >
        <Star
          className={`h-5 w-5 ${
            model.isFavorite
              ? 'text-yellow-500 fill-yellow-500'
              : 'text-foreground-muted hover:text-yellow-500'
          }`}
        />
      </button>
      <Button variant="secondary" size="sm" onClick={handleEdit}>
        Edit
      </Button>
      <DeleteButton onClick={() => setShowDeleteConfirm(true)} />
    </>
  );

  return (
    <div className="flex h-full flex-col">
      {/* Header with back buttons */}
      <DetailHeader
        onBack={handleBack}
        backLabel="Back to Models"
        previousContext={previousContext}
        icon={Box}
        iconSize={12}
        thumbnailUrl={model.thumbnailUrl || undefined}
        title={model.name}
        description={model.description}
        metadata={metadata}
        actions={actions}
      />

      {/* Detail view */}
      <div className="flex-1 overflow-hidden">
        <ModelDetailView
          model={model}
          onNavigate={navigate}
        />
      </div>

      {/* Edit Form */}
      <SlideOver
        open={isEditFormOpen}
        onClose={() => setIsEditFormOpen(false)}
        title="Edit Model"
        description="Update model details"
      >
        <form onSubmit={handleUpdateModel} className="space-y-4">
          <Input
            label="Name"
            value={formData.name || ''}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />

          <Textarea
            label="Description"
            value={formData.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={3}
          />

          <Textarea
            label="Notes"
            value={formData.notes || ''}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={3}
          />

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isFavorite"
              checked={formData.isFavorite || false}
              onChange={(e) => setFormData({ ...formData, isFavorite: e.target.checked })}
              className="h-4 w-4 rounded border-border"
            />
            <label htmlFor="isFavorite" className="text-sm text-foreground">
              Mark as favorite
            </label>
          </div>

          <div className="flex gap-2">
            <Button
              type="submit"
              loading={updateModel.isPending}
              disabled={updateModel.isPending}
              className="flex-1"
            >
              Save Changes
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsEditFormOpen(false)}
              disabled={updateModel.isPending}
            >
              Cancel
            </Button>
          </div>
        </form>
      </SlideOver>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteModel}
        title="Delete Model?"
        description={
          <>
            <p>Are you sure you want to delete "{model.name}"?</p>
            <p className="mt-2">
              This will permanently delete the model and all associated files and links. This action
              cannot be undone.
            </p>
          </>
        }
        confirmText="Delete Model"
        variant="danger"
        loading={deleteModel.isPending}
      />
    </div>
  );
}
